import { useEffect, useState } from "react";
import { Mail, X, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";

const TEMPLATES = [
    { value: "", label: "All templates" },
    { value: "welcome", label: "welcome" },
    { value: "purchase_receipt", label: "purchase_receipt" },
    { value: "phase_unlocked", label: "phase_unlocked" },
    { value: "quiz_passed", label: "quiz_passed" },
    { value: "live_call_reminder", label: "live_call_reminder" },
    { value: "affiliate_approved", label: "affiliate_approved" },
    { value: "commission_earned", label: "commission_earned" },
    { value: "password_reset", label: "password_reset" },
];

const STATUS_COLOR = {
    sent: "#067647",
    logged_only: "#1E48D6",
    failed: "#B42318",
    queued: "#8A94A6",
};

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminEmailLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ template: "", status: "", to: "" });
    const [selected, setSelected] = useState(null);
    const [err, setErr] = useState(null);

    async function load() {
        setLoading(true); setErr(null);
        try {
            const d = await api.adminEmailLog({
                template: filters.template || null,
                status: filters.status || null,
                to: filters.to || null,
                limit: 200,
            });
            setRows(d.rows || []);
        } catch (e) { setErr(e.message || "Failed to load email log."); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.template, filters.status]);

    return (
        <section className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-email-log-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Admin</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Email Log</h1>
                    <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--st-ink-muted)" }}>
                        Every transactional email fired by the platform. If Resend is not configured, status will read
                        <strong> logged_only</strong> — the email content is still recorded here for debugging.
                    </p>
                </div>
                <button type="button" onClick={load}
                        className="btn-outline-navy text-xs inline-flex items-center gap-1"
                        data-testid="email-log-refresh">
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            <div className="mt-6 st-surface p-4 grid gap-3 md:grid-cols-4" data-testid="email-log-filters">
                <div>
                    <label className="st-label">Template</label>
                    <select className="st-input" value={filters.template}
                            onChange={(e) => setFilters({ ...filters, template: e.target.value })}
                            data-testid="email-log-filter-template">
                        {TEMPLATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="st-label">Status</label>
                    <select className="st-input" value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            data-testid="email-log-filter-status">
                        <option value="">Any</option>
                        <option value="sent">sent</option>
                        <option value="logged_only">logged_only</option>
                        <option value="failed">failed</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Recipient email (contains)</label>
                    <div className="flex gap-2">
                        <input className="st-input flex-1" value={filters.to}
                               onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                               placeholder="e.g. @example.com"
                               data-testid="email-log-filter-to" />
                        <button type="button" onClick={load} className="btn-royal text-xs">Apply</button>
                    </div>
                </div>
            </div>

            {err && <div className="mt-4 text-sm" style={{ color: "#B42318" }} data-testid="email-log-err">{err}</div>}
            {loading && <div className="mt-4 text-sm">Loading…</div>}

            {!loading && (
                <div className="mt-6 st-surface p-0 overflow-hidden" data-testid="email-log-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: "var(--st-silver-50)", borderBottom: "1px solid var(--st-line)" }}>
                                <th className="text-left px-4 py-2 font-semibold" style={{ color: "var(--st-ink-muted)" }}>Sent at</th>
                                <th className="text-left px-4 py-2 font-semibold" style={{ color: "var(--st-ink-muted)" }}>Template</th>
                                <th className="text-left px-4 py-2 font-semibold" style={{ color: "var(--st-ink-muted)" }}>To</th>
                                <th className="text-left px-4 py-2 font-semibold" style={{ color: "var(--st-ink-muted)" }}>Subject</th>
                                <th className="text-left px-4 py-2 font-semibold" style={{ color: "var(--st-ink-muted)" }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm"
                                        style={{ color: "var(--st-ink-muted)" }}>No email rows.</td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.created_at + r.to_email + r.template_name}
                                    onClick={() => setSelected(r)}
                                    className="cursor-pointer hover:bg-[var(--st-silver-50)]"
                                    style={{ borderBottom: "1px solid var(--st-line)" }}
                                    data-testid={`email-log-row`}>
                                    <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--st-ink)" }}>{fmt(r.sent_at || r.created_at)}</td>
                                    <td className="px-4 py-2"><span className="font-mono text-xs">{r.template_name}</span></td>
                                    <td className="px-4 py-2 font-mono text-xs">{r.to_email_masked || r.to_email}</td>
                                    <td className="px-4 py-2 truncate" style={{ maxWidth: 260 }}>{r.subject || "—"}</td>
                                    <td className="px-4 py-2">
                                        <span className="text-xs font-semibold" style={{ color: STATUS_COLOR[r.status] || "#8A94A6" }}>
                                            {r.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: "rgba(10, 31, 68, 0.4)" }}
                     onClick={() => setSelected(null)}
                     data-testid="email-log-detail-modal">
                    <div className="max-w-2xl w-full st-surface p-6 max-h-[85vh] overflow-auto"
                         style={{ background: "var(--st-white)" }}
                         onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-royal)" }}>
                                    <Mail size={11} className="inline mr-1" /> {selected.template_name}
                                </div>
                                <div className="mt-1 font-serif text-lg" style={{ color: "var(--st-navy)" }}>{selected.subject || "—"}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="btn-ghost text-xs"><X size={14} /></button>
                        </div>
                        <div className="mt-4 space-y-2 text-xs">
                            <div><strong>To:</strong> <span className="font-mono">{selected.to_email}</span></div>
                            <div><strong>Status:</strong> <span style={{ color: STATUS_COLOR[selected.status] }}>{selected.status}</span></div>
                            <div><strong>Provider ID:</strong> <span className="font-mono">{selected.provider_id || "—"}</span></div>
                            <div><strong>Sent at:</strong> {fmt(selected.sent_at || selected.created_at)}</div>
                            {selected.error && (
                                <div className="mt-3 p-3 rounded" style={{ background: "#FEF3F2", color: "#B42318", border: "1px solid #FECDCA" }}>
                                    <strong>Error:</strong> {selected.error}
                                </div>
                            )}
                            <div className="mt-4">
                                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Context</div>
                                <pre className="mt-2 p-3 rounded text-[11px] whitespace-pre-wrap"
                                     style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)", maxHeight: 300, overflow: "auto" }}>
{JSON.stringify(selected.ctx_snapshot || {}, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
